import Vue from "vue";
import SidebarElementConstants from "./SidebarElementConstants";

const getDefaultState = () => {
  return {
    sidebarElements: [],
    selectedElement: {},

    /** Data loading helpers */
    firstElementsLoadDone: false,
    allElementsLoaded: false,
    loadMoreSkipValue: 0, // Used for skipping items when loading more items
  };
};

const state = Vue.observable(getDefaultState());

const methods = {
  RESET_STATE() {
    Object.assign(state, getDefaultState());
  },

  /** ========== SIDEBAR ELEMENT MANAGER ========== */
  addManyElementsToSidebar(elements) {
    state.sidebarElements = [...state.sidebarElements, ...elements];
  },

  addOneElementToSidebar(element) {
    if (element.groupID) {
      /** Add element to a group if it has a groupID */
      const group = state.sidebarElements.find((el) => el._id === element.groupID);
      if (!Object.hasOwn(group, "groupElements")) {
        /** @NB Must use Vue.set to have reactivity for newly added field. */
        Vue.set(group, "groupElements", [element]);
      } else {
        group.groupElements.push(element);
      }
    } else {
      state.sidebarElements.push(element);
    }
  },

  replaceOneElementInSidebar(replaceID, replacement) {
    if (replacement.groupID) {
      const group = state.sidebarElements.find((el) => el._id === replacement.groupID);
      const index = group.groupElements.findIndex((el) => el._id === replaceID);
      if (index !== -1) {
        group.groupElements.splice(index, 1, replacement);
      }
    } else {
      const index = state.sidebarElements.findIndex((el) => el._id === replaceID);
      if (index !== -1) {
        state.sidebarElements.splice(index, 1, replacement);
      }
    }
  },

  removeOneElementFromSidebar(elementID, groupID = "") {
    if (groupID) {
      const group = state.sidebarElements.find((el) => el._id === groupID);
      const index = group.groupElements.findIndex((el) => el._id === elementID);
      if (index !== -1) {
        group.groupElements.splice(index, 1);
      }
    } else {
      const index = state.sidebarElements.findIndex((el) => el._id === elementID);
      if (index !== -1) {
        state.sidebarElements.splice(index, 1);
      }
    }
  },

  handleSidebarElementsFirstLoad() {
    state.firstElementsLoadDone = true;
  },

  syncLoadMoreSkipValue() {
    state.loadMoreSkipValue = state.sidebarElements.length;
  },

  setAllSidebarElementsLoaded() {
    state.allElementsLoaded = true;
  },

  updateSidebarElementNameByRefID(refID, refName) {
    for (var i = 0; i < state.sidebarElements.length; i++) {
      const element = state.sidebarElements[i];
      if (element.refID && element.refID === refID) {
        Vue.set(state.sidebarElements[i], "refName", refName);
        break;
      }
      if (element.type === SidebarElementConstants.ELEMENT_TYPES.GROUP) {
        for (var k = 0; k < element.groupElements.length; k++) {
          const groupElement = element.groupElements[k];
          if (groupElement.refID && groupElement.refID === refID) {
            Vue.set(state.sidebarElements[i].groupElements[k], "refName", refName);
            break;
          }
        }
      }
    }
  },

  removeSidebarElementByRefID(refID) {
    for (var i = 0; i < state.sidebarElements.length; i++) {
      const element = state.sidebarElements[i];
      if (element.refID && element.refID === refID) {
        state.sidebarElements.splice(i, 1);
        break;
      }
      if (element.type === SidebarElementConstants.ELEMENT_TYPES.GROUP) {
        for (var k = 0; k < element.groupElements.length; k++) {
          const groupElement = element.groupElements[k];
          if (groupElement.refID && groupElement.refID === refID) {
            state.sidebarElements[i].groupElements.splice(k, 1);
            break;
          }
        }
      }
    }
  },

  /**
   * Adds sidebar element to sidebar at the index respective to its orderNr.
   * If inside a group, will use group ID to find the group and put it in
   * the group at the index respective to its orderNr.
   */
  addElementBackToSidebar(element) {
    if (element.groupID) {
      const group = state.sidebarElements.find((el) => el._id === element.groupID);
      const index = group.groupElements.findIndex((el) => el.orderNr >= element.orderNr);
      if (index !== -1) {
        group.groupElements.splice(index, 0, element);
      } else {
        group.groupElements.push(element);
      }
    } else {
      const index = state.sidebarElements.findIndex((el) => el.orderNr >= element.orderNr);
      if (index !== -1) {
        state.sidebarElements.splice(index, 0, element);
      } else {
        state.sidebarElements.push(element);
      }
    }
  },

  findSidebarElementByRefID(refID) {
    for (var i = 0; i < state.sidebarElements.length; i++) {
      const element = state.sidebarElements[i];
      if (element.refID && element.refID === refID) {
        return element;
      }
      if (element.type === SidebarElementConstants.ELEMENT_TYPES.GROUP) {
        for (var k = 0; k < element.groupElements.length; k++) {
          const groupElement = element.groupElements[k];
          if (groupElement.refID && groupElement.refID === refID) {
            return groupElement;
          }
        }
      }
    }
  },
};

const SidebarElementStore = {
  state,
  methods,
};

export default SidebarElementStore;
